import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { Badge, Button, Card, Empty, Field, inputClass } from "@/components/ui";
import { useToast } from "@/components/Toast";
import {
  api,
  type LeadDetail,
  type OfferChannel,
  type OfferRow,
  type OfferStatus,
} from "@/lib/api";
import { parseDollars, useModalKeys } from "@/lib/dialog";
import { formatDate, formatMoneyCents } from "@/lib/format";
import { STAGE_LABEL } from "@/lib/stages";
import { cn } from "@/lib/utils";

/**
 * v2.3 P1.1/P1.2 (D-74, D-75): the offer stage holds its numbers.
 *
 * Before this, a lead in `offer` showed "No offers yet. Every offer references a comp set." with no way
 * to log one, and Pricing's three amounts were read-only — the seller's $1,000,000 ask lived only inside
 * a pending agent suggestion. Now: "Log offer" here and on Pricing; each offer's status changes inline;
 * Asking / Seller counter / Seller owes are click-to-edit; a pending suggestion's amount shows greyed
 * with an Apply for that one field.
 */

const OFFER_STATUSES: OfferStatus[] = [
  "draft",
  "sent",
  "countered",
  "accepted",
  "rejected",
  "withdrawn",
];
const OFFER_CHANNELS: OfferChannel[] = ["sms", "call", "email", "mail"];

const STATUS_TONE: Record<string, "muted" | "info" | "warn" | "ok" | "danger"> = {
  draft: "muted",
  sent: "info",
  countered: "warn",
  accepted: "ok",
  rejected: "danger",
  withdrawn: "muted",
  expired: "muted",
};

/** An early lead that a number moves to Offer — the UI asks for the next action first (D-31). */
function movesToOffer(stage: string): boolean {
  return stage === "intake" || stage === "follow_up";
}

function dollars(cents: number | null | undefined): string {
  return cents === null || cents === undefined ? "" : String(cents / 100);
}

function localInput(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function defaultDue(): string {
  const d = new Date();
  d.setDate(d.getDate() + 2);
  d.setHours(9, 0, 0, 0);
  return localInput(d.toISOString());
}

/** The next-action pair a move to Offer needs, prefilled from the lead's own. */
function NextActionFields({
  action,
  due,
  setAction,
  setDue,
  onEnter,
  testPrefix,
}: {
  action: string;
  due: string;
  setAction: (v: string) => void;
  setDue: (v: string) => void;
  onEnter?: () => void;
  testPrefix: string;
}) {
  return (
    <div className="space-y-2 rounded border border-brand/30 bg-brand/5 p-2">
      <p className="text-[11px] text-fg-muted">
        This puts a number on the table, so the lead moves to {STAGE_LABEL.offer}. Offer needs a next
        action and a due date.
      </p>
      <Field label="Next action" required>
        <input
          value={action}
          onChange={(e) => setAction(e.target.value)}
          className={inputClass}
          data-testid={`${testPrefix}-next-action`}
        />
      </Field>
      <Field label="Due" required>
        <input
          type="datetime-local"
          value={due}
          onChange={(e) => setDue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && onEnter) {
              e.preventDefault();
              onEnter();
            }
          }}
          className={inputClass}
          data-testid={`${testPrefix}-next-action-due`}
        />
      </Field>
    </div>
  );
}

/** Log a new offer, or edit one. */
export function OfferDialog({
  lead,
  offer,
  onClose,
}: {
  lead: LeadDetail;
  offer?: OfferRow;
  onClose: () => void;
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const box = useRef<HTMLDivElement>(null);
  useModalKeys(box, onClose);
  const [amount, setAmount] = useState(dollars(offer?.amount_cents));
  const [channel, setChannel] = useState<OfferChannel>(
    (offer?.channel as OfferChannel | undefined) ?? "sms",
  );
  const [status, setStatus] = useState<OfferStatus>((offer?.status as OfferStatus | undefined) ?? "sent");
  const [sentAt, setSentAt] = useState(localInput(offer?.sent_at) || localInput(new Date().toISOString()));
  const [counter, setCounter] = useState(dollars(offer?.counter_cents));
  const [notes, setNotes] = useState(offer?.notes ?? "");
  const [action, setAction] = useState(lead.lead.next_action ?? "");
  const [due, setDue] = useState(localInput(lead.lead.next_action_due) || defaultDue());

  const asks =
    movesToOffer(lead.lead.stage) &&
    (status === "sent" || status === "countered") &&
    !(offer && (offer.status === "sent" || offer.status === "countered"));
  const amountCents = parseDollars(amount);
  const counterCents = counter.trim() ? parseDollars(counter) : null;
  const ready =
    amountCents !== null &&
    amountCents > 0 &&
    (!counter.trim() || counterCents !== null) &&
    (!asks || Boolean(action.trim() && due));

  const save = useMutation({
    mutationFn: () => {
      const common = {
        amount_cents: amountCents!,
        channel,
        status,
        sent_at: status === "draft" ? null : new Date(sentAt).toISOString(),
        counter_cents: counterCents,
        notes: notes.trim() || null,
        ...(asks ? { next_action: action, next_action_due: new Date(due).toISOString() } : {}),
      };
      return offer ? api.updateOffer(offer.id, common) : api.createOffer(lead.lead.id, common);
    },
    onSuccess: () => {
      toast.push(offer ? "Offer updated" : `Offer of ${formatMoneyCents(amountCents)} logged`);
      void qc.invalidateQueries();
      onClose();
    },
    onError: (e: Error) => toast.push(e.message, "danger"),
  });

  function submit() {
    if (ready && !save.isPending) save.mutate();
  }

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={offer ? "Edit offer" : "Log offer"}
      data-testid="offer-dialog"
    >
      <div ref={box} className="w-full max-w-md rounded-[var(--radius-card)] border bg-surface p-4">
        <h2 className="mb-3 text-sm font-semibold">{offer ? "Edit offer" : "Log offer"}</h2>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <div className="grid grid-cols-2 gap-2">
            <Field label="Amount ($)" required>
              <input
                data-autofocus
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                inputMode="decimal"
                placeholder="45k"
                className={inputClass}
                data-testid="offer-amount"
              />
            </Field>
            <Field label="Channel">
              <select
                value={channel}
                onChange={(e) => setChannel(e.target.value as OfferChannel)}
                className={inputClass}
                data-testid="offer-channel"
              >
                {OFFER_CHANNELS.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Status">
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as OfferStatus)}
                className={inputClass}
                data-testid="offer-status"
              >
                {OFFER_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Sent">
              <input
                type="datetime-local"
                value={sentAt}
                disabled={status === "draft"}
                onChange={(e) => setSentAt(e.target.value)}
                className={inputClass}
                data-testid="offer-sent-at"
              />
            </Field>
            <Field label="Their counter ($)">
              <input
                value={counter}
                onChange={(e) => setCounter(e.target.value)}
                inputMode="decimal"
                placeholder="optional"
                className={inputClass}
                data-testid="offer-counter"
              />
            </Field>
          </div>
          <Field label="Notes">
            <input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className={inputClass}
              placeholder="optional — the comp set can be attached later"
              data-testid="offer-notes"
            />
          </Field>
          {asks && (
            <NextActionFields
              action={action}
              due={due}
              setAction={setAction}
              setDue={setDue}
              onEnter={submit}
              testPrefix="offer"
            />
          )}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={!ready || save.isPending}
              data-testid="offer-save"
            >
              {save.isPending ? "Saving…" : offer ? "Save offer" : "Log offer"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

/** The Offers tab. */
export function OffersPanel({ lead }: { lead: LeadDetail }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [dialog, setDialog] = useState<{ offer?: OfferRow } | null>(null);
  const offers = lead.offers;

  const setStatus = useMutation({
    mutationFn: ({ offer, status }: { offer: OfferRow; status: OfferStatus }) =>
      api.updateOffer(offer.id, { status }),
    onSuccess: (_d, v) => {
      toast.push(`Offer marked ${v.status}`);
      void qc.invalidateQueries();
    },
    onError: (e: Error) => toast.push(e.message, "danger"),
  });
  const remove = useMutation({
    mutationFn: (offer: OfferRow) => api.deleteOffer(offer.id),
    onSuccess: () => {
      toast.push("Offer deleted");
      void qc.invalidateQueries();
    },
    onError: (e: Error) => toast.push(e.message, "danger"),
  });

  return (
    <Card
      title={`Offers (${offers.length})`}
      action={
        <Button size="sm" variant="primary" onClick={() => setDialog({})} data-testid="log-offer">
          Log offer
        </Button>
      }
    >
      {dialog && <OfferDialog lead={lead} offer={dialog.offer} onClose={() => setDialog(null)} />}
      {offers.length === 0 ? (
        <Empty
          title="No offers yet."
          hint="Log the number you sent, or theirs. A comp set is optional and can be attached later."
        />
      ) : (
        <table className="w-full text-xs" data-testid="offers-table">
          <thead>
            <tr className="border-b text-left text-fg-muted">
              <th className="py-1">Amount</th>
              <th className="py-1">Channel</th>
              <th className="py-1">Status</th>
              <th className="py-1">Sent</th>
              <th className="py-1">Counter</th>
              <th className="py-1">Notes</th>
              <th className="py-1" />
            </tr>
          </thead>
          <tbody>
            {offers.map((o) => (
              <tr key={o.id} className="border-b last:border-b-0" data-testid="offer-row">
                <td className="py-1 tabular-nums font-medium">{formatMoneyCents(o.amount_cents)}</td>
                <td className="py-1">{o.channel}</td>
                <td className="py-1">
                  <span className="inline-flex items-center gap-1">
                    <Badge tone={STATUS_TONE[o.status] ?? "muted"}>{o.status}</Badge>
                    <select
                      aria-label="Change status"
                      value={o.status}
                      onChange={(e) => setStatus.mutate({ offer: o, status: e.target.value as OfferStatus })}
                      className="rounded border bg-surface px-1 py-0.5 text-[11px]"
                      data-testid="offer-row-status"
                    >
                      {OFFER_STATUSES.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </span>
                </td>
                <td className="py-1">{formatDate(o.sent_at)}</td>
                <td className="py-1 tabular-nums">{formatMoneyCents(o.counter_cents)}</td>
                <td className="max-w-[12rem] truncate py-1 text-fg-muted" title={o.notes ?? undefined}>
                  {o.notes ?? ""}
                </td>
                <td className="py-1 text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" onClick={() => setDialog({ offer: o })} data-testid="offer-edit">
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      if (window.confirm(`Delete the ${formatMoneyCents(o.amount_cents)} offer?`)) remove.mutate(o);
                    }}
                    data-testid="offer-delete"
                  >
                    Delete
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

type MoneyKey = "asking_price_cents" | "seller_counter_cents" | "seller_owes_cents";

/** One click-to-edit money row: Enter saves, Esc cancels, ledgered as field_changed by the API. */
function MoneyRow({
  lead,
  field,
  label,
  suggested,
}: {
  lead: LeadDetail;
  field: MoneyKey;
  label: string;
  /** A PENDING suggestion's value for this field, shown greyed with Apply. */
  suggested?: number | null;
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const current = lead[field] ?? null;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(dollars(current));
  const [asking, setAsking] = useState<null | "type" | "apply">(null);
  const [action, setAction] = useState(lead.lead.next_action ?? "");
  const [due, setDue] = useState(localInput(lead.lead.next_action_due) || defaultDue());
  const input = useRef<HTMLInputElement>(null);
  // Seller owes is not a number on the table; asking and counter are (D-31).
  const moves = field !== "seller_owes_cents" && movesToOffer(lead.lead.stage);

  useEffect(() => setDraft(dollars(current)), [current]);
  useEffect(() => {
    if (editing) input.current?.focus();
  }, [editing]);

  const extra = () => (moves ? { next_action: action, next_action_due: new Date(due).toISOString() } : {});

  const save = useMutation({
    mutationFn: (value: number | null) => api.patchLead(lead.lead.id, { [field]: value, ...extra() }),
    onSuccess: (detail) => {
      setEditing(false);
      setAsking(null);
      toast.push(
        detail.lead.stage !== lead.lead.stage
          ? `${label} saved — moved to ${STAGE_LABEL[detail.lead.stage]}`
          : `${label} saved`,
      );
      void qc.invalidateQueries();
    },
    onError: (e: Error) => toast.push(e.message, "danger"),
  });
  const apply = useMutation({
    mutationFn: () =>
      api.applySuggestionField(lead.lead.id, {
        field: field as "asking_price_cents" | "seller_counter_cents",
        ...extra(),
      }),
    onSuccess: () => {
      setAsking(null);
      toast.push(`${label} applied from the suggestion`);
      void qc.invalidateQueries();
    },
    onError: (e: Error) => toast.push(e.message, "danger"),
  });

  function commit() {
    const trimmed = draft.trim();
    const value = trimmed === "" ? null : parseDollars(trimmed);
    if (trimmed !== "" && value === null) {
      toast.push(`${label}: "${draft}" is not an amount`, "danger");
      return;
    }
    if (value === current) {
      setEditing(false);
      return;
    }
    if (moves && value && asking !== "type") {
      setAsking("type");
      return;
    }
    save.mutate(value);
  }

  const showSuggestion =
    suggested !== null && suggested !== undefined && suggested > 0 && suggested !== current;

  return (
    <div data-testid={`pricing-${field}`}>
      <div className="flex items-center justify-between gap-2">
        <dt className="text-fg-muted">{label}</dt>
        <dd className="tabular-nums">
          {editing ? (
            <input
              ref={input}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  commit();
                }
                if (e.key === "Escape") {
                  e.preventDefault();
                  e.stopPropagation();
                  setDraft(dollars(current));
                  setEditing(false);
                  setAsking(null);
                }
              }}
              inputMode="decimal"
              aria-label={label}
              className={inputClass + " h-6 w-28 text-right text-xs"}
              data-testid={`pricing-input-${field}`}
            />
          ) : (
            <button
              type="button"
              onClick={() => setEditing(true)}
              title="Click to edit"
              className={cn("rounded px-1 hover:bg-surface-2", current === null && "text-fg-muted")}
              data-testid={`pricing-edit-${field}`}
            >
              {formatMoneyCents(current)}
            </button>
          )}
        </dd>
      </div>
      {showSuggestion && !editing && (
        <div className="flex items-center justify-end gap-1 text-[11px] text-fg-muted" data-testid={`pricing-suggested-${field}`}>
          <span title="From a pending agent suggestion — not applied">suggested {formatMoneyCents(suggested)}</span>
          <button
            type="button"
            className="rounded border px-1 hover:bg-surface-2 hover:text-fg"
            onClick={() => (moves ? setAsking("apply") : apply.mutate())}
            disabled={apply.isPending}
            data-testid={`pricing-apply-${field}`}
          >
            Apply
          </button>
        </div>
      )}
      {asking && (
        <div className="mt-1 space-y-1">
          <NextActionFields
            action={action}
            due={due}
            setAction={setAction}
            setDue={setDue}
            onEnter={() => (asking === "apply" ? apply.mutate() : save.mutate(parseDollars(draft)))}
            testPrefix={`pricing-${field}`}
          />
          <div className="flex justify-end gap-1">
            <Button size="sm" variant="ghost" onClick={() => { setAsking(null); setEditing(false); setDraft(dollars(current)); }}>
              Cancel
            </Button>
            <Button
              size="sm"
              variant="primary"
              disabled={!(action.trim() && due) || save.isPending || apply.isPending}
              onClick={() => (asking === "apply" ? apply.mutate() : save.mutate(parseDollars(draft)))}
              data-testid={`pricing-confirm-${field}`}
            >
              Save and move to {STAGE_LABEL.offer}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/** The Pricing card: anchor band, the three editable amounts, the last offer and "Log offer". */
export function PricingCard({ lead }: { lead: LeadDetail }) {
  const [logging, setLogging] = useState(false);
  const pending = lead.lead.agent_suggestion_status === "pending";
  const { data: suggestion } = useQuery({
    queryKey: ["suggestion", lead.lead.id],
    queryFn: () => api.suggestion(lead.lead.id),
    enabled: pending,
  });
  const s = (pending ? suggestion?.suggestion : null);
  const num = (v: unknown) => (typeof v === "number" && v > 0 ? v : null);
  const suggestedAsking = num(s?.asking_price_cents);
  const suggestedCounter = num(s?.seller_counter_cents) ?? num(s?.counter_cents);

  return (
    <Card
      title="Pricing"
      data-testid="pricing-card"
      action={
        <Button size="sm" onClick={() => setLogging(true)} data-testid="pricing-log-offer">
          Log offer
        </Button>
      }
    >
      {logging && <OfferDialog lead={lead} onClose={() => setLogging(false)} />}
      <dl className="space-y-1 text-xs">
        <div className="flex justify-between gap-2">
          <dt className="text-fg-muted">
            Anchor band ({lead.anchor.low_pct}–{lead.anchor.high_pct}% of CAD)
          </dt>
          <dd className="tabular-nums" data-testid="pricing-anchor">
            {lead.anchor.basis_cents ? (
              <>
                {formatMoneyCents(lead.anchor.low_cents)} – {formatMoneyCents(lead.anchor.high_cents)}
              </>
            ) : (
              <span className="text-fg-muted">enter assessed value</span>
            )}
          </dd>
        </div>
        <MoneyRow lead={lead} field="asking_price_cents" label="Asking" suggested={suggestedAsking} />
        <MoneyRow lead={lead} field="seller_counter_cents" label="Seller counter" suggested={suggestedCounter} />
        <MoneyRow lead={lead} field="seller_owes_cents" label="Seller owes" />
        <div className="flex justify-between gap-2">
          <dt className="text-fg-muted">Last offer</dt>
          <dd className="tabular-nums" data-testid="pricing-last-offer">
            {formatMoneyCents(lead.last_offer_cents)}
          </dd>
        </div>
      </dl>
    </Card>
  );
}
